import { debugConsole, WebauthnPrfSupportStatus } from 'backend-client-web';
import { CredentialCreateResult, CredentialGetResult } from 'client-core';
import { toArrayBufferBacked } from 'vault-crypto';

export function extractCredentialCreateResult(cred: Credential | null): CredentialCreateResult {
  debugConsole.info('webauthn.create credential', cred);

  if (!cred) {
    throw new Error(`Unexpectedly received no credential`);
  }
  if (!(cred instanceof PublicKeyCredential)) {
    throw new Error(`Unexpected credential type ${cred?.type}`);
  }
  if (!(cred.response instanceof AuthenticatorAttestationResponse)) {
    throw new Error('Unexpected AssertionResponse type');
  }

  const { prf } = cred.getClientExtensionResults();

  return {
    id: cred.id,
    rawId: new Uint8Array(cred.rawId),
    response: {
      attestationObject: new Uint8Array(cred.response.attestationObject),
      clientDataJSON: new Uint8Array(cred.response.clientDataJSON),
      transports: cred.response.getTransports ? cred.response.getTransports() : undefined,
    },
    prfSupportStatus:
      prf ?
        prf.enabled ?
          WebauthnPrfSupportStatus.AVAILABLE
        : WebauthnPrfSupportStatus.UNAVAILABLE
      : WebauthnPrfSupportStatus.UNKNOWN,
    // NOTE: Hardware support not available at the time of writing (2023-09-25), but it's available
    // by browser spec, so we include it here for completeness.
    prfExtensionResult:
      prf?.results ?
        {
          first: toUint8Array(prf.results.first),
          second: prf.results.second ? toUint8Array(prf.results.second) : undefined,
        }
      : undefined,
  };
}

export function extractCredentialGetResult(cred: Credential | null): CredentialGetResult {
  debugConsole.info('webauthn.get credential', cred);

  if (!cred) {
    throw new Error(`Unexpectedly received no credential`);
  }
  if (!(cred instanceof PublicKeyCredential)) {
    throw new Error(`Unexpected credential type ${cred?.type}`);
  }
  if (!(cred.response instanceof AuthenticatorAssertionResponse)) {
    throw new Error('Unexpected AssertionResponse type');
  }

  const { prf } = cred.getClientExtensionResults();

  return {
    id: cred.id,
    rawId: new Uint8Array(cred.rawId),
    response: {
      authenticatorData: new Uint8Array(cred.response.authenticatorData),
      clientDataJSON: new Uint8Array(cred.response.clientDataJSON),
      signature: new Uint8Array(cred.response.signature),
      userHandle: cred.response.userHandle ? new Uint8Array(cred.response.userHandle) : undefined,
    },
    prfExtensionResult:
      prf?.results ?
        {
          first: toUint8Array(prf.results.first),
          second: prf.results.second ? toUint8Array(prf.results.second) : undefined,
        }
      : undefined,
  };
}

export function toUint8Array(src: BufferSource): Uint8Array<ArrayBuffer> {
  if (src instanceof ArrayBuffer) {
    return new Uint8Array(src);
  }
  return toArrayBufferBacked(new Uint8Array(src.buffer, src.byteOffset, src.byteLength));
}
